import * as React from 'react';
import {Link} from 'react-router-dom';
import LegalPage, {LEGAL_ADDRESS, LEGAL_ENTITY} from '../components/LegalPage';
import {BRAND} from '../brand';

// Linked from the Facebook app's "Terms of Service URL" setting.
const TermsOfServicePage: React.FC = () => (
    <LegalPage eyebrow="Legal" title="Terms of Service" updated="October 6, 2026">
        <section>
            <p>
                These terms cover your use of this website and the services you book through it from
                {' '}{LEGAL_ENTITY} ("{BRAND.name}", "we"). By using the site, signing in, or booking with us, you agree
                to them. If you don't agree, please don't use the site.
            </p>
        </section>

        <section>
            <h2>Your account</h2>
            <ul>
                <li>
                    You can sign in with Facebook. You're responsible for activity under your sign-in, so keep your
                    Facebook account secure and tell us if you think someone else has used it.
                </li>
                <li>You must be at least 18, or have a parent or guardian agree to these terms for you.</li>
                <li>Give us accurate contact and billing details and keep them up to date.</li>
                <li>
                    We may suspend or close an account that breaks these terms or misuses the site. You can stop
                    using the site at any time and ask us to delete your data (see
                    {' '}<Link to="/data-deletion">Data Deletion</Link>).
                </li>
            </ul>
        </section>

        <section>
            <h2>Quotes and projects</h2>
            <p>
                Requests you send through the site (production, photography, 3D scanning, printing, marketing,
                live events, and similar work) are inquiries, not contracts. We reply with a quote; the work is
                agreed only when you accept a quote. Accepted work is invoiced as stated in the quote, and the
                quote's scope, price, schedule, and deliverables take precedence over these terms where they
                differ. Changes to scope after acceptance may change the price.
            </p>
        </section>

        <section id="rentals">
            <h2>Equipment rentals</h2>
            <ul>
                <li>
                    A rental request isn't a booking until we confirm it. Prices, availability, and any deposit are
                    as shown on your confirmed reservation.
                </li>
                <li>
                    You can cancel a request or confirmed booking online up until your scheduled pickup time. After that,
                    contact us; cancellation fees may apply.
                </li>
                <li>
                    Check the gear when you pick it up and tell us right away about anything missing or damaged.
                    From pickup until we check it back in, you're responsible for the equipment, including loss,
                    theft, and damage beyond normal wear, and you'll pay for repair or replacement at our current
                    cost if that happens.
                </li>
                <li>
                    Return everything on time, clean, and in the condition you received it. Late returns are charged
                    for the extra time, and gear that isn't returned may be treated as lost.
                </li>
                <li>
                    Use the equipment lawfully, only for the purpose you booked it for, and don't lend or sublet it.
                    We may require proof of insurance before releasing higher-value gear.
                </li>
            </ul>
        </section>

        <section>
            <h2>Talent and crew</h2>
            <p>
                Crew profiles on the site describe photographers, videographers, editors, and other creatives we
                work with. Rates and availability shown are a guide; crew are booked through us, under a quote you
                accept. Crew who connect their Facebook profile or Page to their listing confirm that they own or
                manage it and agree that we may show its name, link, and profile picture on their listing. They
                can disconnect it or ask us to remove it at any time.
            </p>
        </section>

        <section>
            <h2>Payment</h2>
            <p>
                Amounts are due as stated on your invoice or reservation. Payments are handled by our payment
                processor, and we don't store full card numbers. We may hold or cancel a booking that hasn't been
                paid as agreed.
            </p>
        </section>

        <section>
            <h2>Your content</h2>
            <p>
                You keep ownership of files and materials you send us (for example, 3D models, reference images,
                or briefs). You give us permission to use them only to quote and deliver your work, and you confirm
                you have the rights to share them. Ownership and usage rights in the work we deliver are set out in
                your quote; unless it says otherwise, we may show the finished work in our portfolio.
            </p>
        </section>

        <section>
            <h2>Acceptable use</h2>
            <p>
                Don't use the site to break the law, upload anything you don't have the right to share, try to
                access accounts or areas that aren't yours (including staff pages), interfere with the site's
                operation, or scrape it in bulk.
            </p>
        </section>

        <section>
            <h2>Facebook</h2>
            <p>
                Signing in with Facebook is also governed by Facebook's own terms. Facebook isn't responsible for
                our services. What we receive from Facebook and how we use it is described in our
                {' '}<Link to="/privacy">Privacy Policy</Link>.
            </p>
        </section>

        <section>
            <h2>Disclaimers and liability</h2>
            <p>
                The site is provided "as is". We work hard to keep it accurate and available, but we don't promise
                it will be error-free or always online. To the fullest extent the law allows, we aren't liable for
                indirect or consequential losses (such as lost profits or lost shoots), and our total liability for
                any claim is limited to the amount you paid us for the service the claim relates to. Nothing in
                these terms limits liability that can't be limited by law.
            </p>
        </section>

        <section>
            <h2>Changes and governing law</h2>
            <p>
                We may update these terms; the date at the top shows the latest version, and changes apply from
                then on (not to bookings already confirmed). These terms are governed by the laws of the State of
                Washington, and disputes will be handled in the courts of Clark County, Washington.
            </p>
        </section>

        <section>
            <h2>Contact</h2>
            <p>
                Questions about these terms: reach us through our <Link to="/contact">contact page</Link>, or write
                to {LEGAL_ENTITY}, {LEGAL_ADDRESS}.
            </p>
        </section>
    </LegalPage>
);

export default TermsOfServicePage;
